import React, { Component } from "react";
import { AxiosResponse } from "axios";
import ReactHtmlParser from "react-html-parser";
import { getRequest, putRequest } from "../../../utils/httpRequest";
import {
  NotificationResource,
  openNotification as followNotification,
} from "../../../utils/notifications";
import AlertIcon from "../../Notifications/AlertIcon";
import { CurrentUser } from "../../../stores/CurrentUserStore/Types";

interface AchievementResponse {
  page: Achievement[];
  total_count: number;
}

interface Achievement {
  id: number;
  html: string;
  xp: number;
  created_at: string;
  opened_xp: boolean;
  created_at_timestamp: string;
}

interface Props {
  getCurrentUser: () => void;
  currentUser: CurrentUser;
  handleShowNotificationModal: (val: boolean) => void;
  showNotificationModal: boolean;
}

interface State {
  notifications: NotificationResource[];
  xp_alerts: Achievement[];
  xpAlertsTotalCount: number;
  loading: boolean;
  activePanel: "notifications" | "xp";
}

const NOTIFICATION_PREVIEW_LIMIT = 4;
const XP_PREVIEW_LIMIT = 3;

class Notifications extends Component<Props, State> {
  dropdownRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.dropdownRef = React.createRef();

    this.state = {
      notifications: [],
      xp_alerts: [],
      loading: false,
      xpAlertsTotalCount: 0,
      activePanel: "notifications",
    };
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    this.setState({ loading: true });
    return getRequest("/api/notifications.json").then(
      (response: AxiosResponse<NotificationResource[]>) => {
        this.setState({ notifications: response.data });
        this.fetchXpAlerts();
      },
    );
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  }

  handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (!this.props.showNotificationModal) {
      return;
    }

    if (this.dropdownRef.current?.contains(target)) {
      return;
    }

    if (target.closest(".notif-bell")) {
      return;
    }

    this.props.handleShowNotificationModal(false);
  };

  computeXpAlertsLastDateTimeAsUrlEncodedString = (): string => {
    const xpAlertsLength = this.state.xp_alerts.length;
    const lastDatetimeString =
      (xpAlertsLength > 0 &&
        this.state.xp_alerts[xpAlertsLength - 1].created_at_timestamp) ||
      "";
    return encodeURIComponent(lastDatetimeString);
  };

  fetchXpAlerts = () => {
    this.setState({ loading: true });

    return getRequest(
      `/api/achievements/xp/recent.json?last_datetime=${this.computeXpAlertsLastDateTimeAsUrlEncodedString()}`,
    ).then((response: AxiosResponse<AchievementResponse>) => {
      const newXpAlerts = this.state.xp_alerts.concat(response.data.page);
      this.setState({
        xp_alerts: newXpAlerts,
        loading: false,
        xpAlertsTotalCount: response.data.total_count,
      });
    });
  };

  updateOpenNotifications = () => {
    return putRequest("/api/notifications/update_all.json", {}).then(() => {
      putRequest(
        `/api/achievements/xp/mark_as_opened.json?last_datetime=${this.computeXpAlertsLastDateTimeAsUrlEncodedString()}`,
        {},
      );
      this.props.getCurrentUser();
      this.props.handleShowNotificationModal(false);
    });
  };

  markAllAsRead = () => {
    this.setState({ loading: true });

    return putRequest("/api/notifications/update_all.json", {})
      .then(() => {
        this.setState(({ notifications, xp_alerts }) => ({
          notifications: notifications.map((notification) => ({
            ...notification,
            opened: true,
          })),
          xp_alerts: xp_alerts.map((alert) => ({ ...alert, opened_xp: true })),
          loading: false,
        }));
        this.props.getCurrentUser();
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  setActivePanel = (activePanel: "notifications" | "xp") => {
    this.setState({ activePanel });
  };

  openNotification = (
    event: React.MouseEvent<HTMLAnchorElement>,
    notification: NotificationResource,
  ) => {
    event.preventDefault();
    followNotification(notification, this.props.getCurrentUser);
  };

  renderNotifications = () => {
    const { notifications, loading } = this.state;

    if (notifications.length > 0) {
      return (
        <>
          {notifications
            .slice(0, NOTIFICATION_PREVIEW_LIMIT)
            .map((notification) => (
              <a
                className={`notif-item ${!notification.opened ? "is-unread" : ""} ${!notification.href ? "no-href" : ""}`}
                href={notification.href || "javascript:void(0)"}
                onClick={(event) =>
                  notification.href &&
                  this.openNotification(event, notification)
                }
                key={notification.id}
              >
                <AlertIcon notification={notification} baseClass="notif-ic" />
                <span className="notif-body">
                  <span className="notif-text">
                    <strong>
                      {/* @ts-ignore */}
                      {ReactHtmlParser(notification.title)}
                    </strong>
                    {notification.body && (
                      <>
                        {" "}
                        {/* @ts-ignore */}
                        {ReactHtmlParser(notification.body)}
                      </>
                    )}
                  </span>
                  {notification.target_name && (
                    <span className="notif-target">
                      {/* @ts-ignore */}
                      {ReactHtmlParser(notification.target_name)}
                    </span>
                  )}
                  <span className="notif-time">
                    {/* @ts-ignore */}
                    {ReactHtmlParser(notification.created_at)}
                  </span>
                </span>
              </a>
            ))}
        </>
      );
    }

    return (
      <>
        {!loading && (
          <div className="no-notifications">
            <h3>You're all caught up.</h3>
            <p>You have no notifications.</p>
          </div>
        )}
      </>
    );
  };

  renderXpAlerts = () => {
    const { xp_alerts, loading } = this.state;
    const { currentUser } = this.props;

    if (xp_alerts.length > 0) {
      return (
        <>
          <div className="xp-total">
            <div className="n">{currentUser.xp.toLocaleString()} XP</div>
            <div className="l">Total earned, all time</div>
          </div>
          {xp_alerts.slice(0, XP_PREVIEW_LIMIT).map((alert) => (
            <div className="notif-item notif-item--xp" key={alert.id}>
              <span className="notif-body">
                <span className="notif-text">
                  {!alert.opened_xp && <span className="notif-new">New</span>}
                  <strong>+{alert.xp}</strong> {/* @ts-ignore */}
                  {ReactHtmlParser(alert.html)}
                </span>
                <span className="notif-time">
                  {/* @ts-ignore */}
                  {ReactHtmlParser(alert.created_at)}
                </span>
              </span>
            </div>
          ))}
        </>
      );
    }

    return (
      <>
        {!loading && (
          <>
            <div className="xp-total">
              <div className="n">{currentUser.xp.toLocaleString()} XP</div>
              <div className="l">Total earned, all time</div>
            </div>
            <div className="no-notifications">
              <h3>No XP yet.</h3>
              <p>Your wins will show up here as you learn.</p>
            </div>
          </>
        )}
      </>
    );
  };

  render() {
    const { loading, activePanel } = this.state;
    const { showNotificationModal } = this.props;

    return (
      <div
        ref={this.dropdownRef}
        className={`notif-dropdown ${showNotificationModal ? "is-open" : ""}`}
        id="notifDropdown"
        role="dialog"
        aria-label="Your alerts"
        aria-hidden={!showNotificationModal}
      >
        <div className="notif-head">
          <h3>Your alerts</h3>
          <button
            className="notif-mark"
            type="button"
            onClick={() => this.markAllAsRead()}
          >
            Mark all read
          </button>
        </div>
        <div className="notif-tabs" role="tablist">
          <button
            className="notif-tab"
            role="tab"
            aria-selected={activePanel === "notifications"}
            aria-controls="np-alerts"
            id="nt-alerts"
            type="button"
            onClick={() => this.setActivePanel("notifications")}
          >
            Notifications
          </button>
          <button
            className="notif-tab"
            role="tab"
            aria-selected={activePanel === "xp"}
            aria-controls="np-xp"
            id="nt-xp"
            type="button"
            onClick={() => this.setActivePanel("xp")}
          >
            XP &amp; Wins
          </button>
        </div>
        {loading && (
          <div className="notif-loading">
            <div className="ui active centered inline loader" />
          </div>
        )}
        <div
          className="notif-panel"
          id="np-alerts"
          role="tabpanel"
          aria-labelledby="nt-alerts"
          hidden={activePanel !== "notifications"}
        >
          {this.renderNotifications()}
        </div>
        <div
          className="notif-panel"
          id="np-xp"
          role="tabpanel"
          aria-labelledby="nt-xp"
          hidden={activePanel !== "xp"}
        >
          {this.renderXpAlerts()}
        </div>
        <div className="notif-foot">
          <a href="/notifications">View all alerts</a>
        </div>
      </div>
    );
  }
}

export default Notifications;
